import { Centered, EmptyState, FlexColumn, MaterialIcon, useMoment } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { Spinner } from 'baseui/spinner';
import { ParagraphSmall } from 'baseui/typography';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { PATHS_BY_GROUP_TABLE_COLUMNS } from '../../../constants/paths';
import { useStyles } from '../../../hooks/useStyles';
import { getPathsByGroup } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import {
  pathDistanceFilterState,
  pathsState,
  pathTimeFilterState,
  reportEndDateState,
  reportFilterGroupState,
  reportStartDateState,
  selectedPathIdState,
} from '../../../storage/reports';
import { PathItemByGroup } from '../../../types/paths';
import { formatDurationFromSeconds } from '../../../utils/dates';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import { StyledTooltip } from '../../ui/StyledTooltip';

export function PathListByGroup() {
  const { css } = useStyles();
  const groupId = useRecoilValue(reportFilterGroupState);
  const apiParams = useRecoilValue(apiParamsState);
  const [paths, setPaths] = useRecoilState(pathsState);
  const [selectedPathId, setSelectedPathId] = useRecoilState(selectedPathIdState);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { formatDateFromTs } = useMoment();
  const [loading, setLoading] = useState(false);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const endDate = useRecoilValue(reportEndDateState);
  const startDate = useRecoilValue(reportStartDateState);
  const pathTimeFilter = useRecoilValue(pathTimeFilterState);
  const pathDistanceFilter = useRecoilValue(pathDistanceFilterState);

  useEffect(() => {
    setPage(0);
  }, [pathTimeFilter, pathDistanceFilter]);

  useEffect(() => {
    if ((startDate && !endDate) || (endDate && !startDate)) {
      setPaths(undefined);
      setTotalItems(0);
      return;
    }
    if (page === 0) {
      setPage(1);
      return;
    }
    setLoading(true);
    const loadPaths = async () => {
      if (!apiParams) return;
      const { error, paths, total } = await getPathsByGroup(
        apiParams.clientId,
        groupId,
        page,
        isAllWorkspacesSelected ? undefined : apiParams.subClientId,
        startDate,
        endDate,
        pathTimeFilter * 60 || 300,
        pathDistanceFilter || 700,
      );
      if (!error && paths && total) {
        setPaths(paths);
        setTotalItems(total);
      } else if (error) {
        setPaths(undefined);
        setTotalItems(0);
      }
      setLoading(false);
    };
    loadPaths();

    return () => {
      setPaths(undefined);
    };
  }, [page, groupId, startDate, endDate, apiParams?.clientId, apiParams?.subClientId, isAllWorkspacesSelected]);

  if (!groupId) {
    return <EmptyState title="Selecciona un grupo para visualizar sus trayectos." />;
  }

  return (
    <FlexColumn
      gap={0}
      classNames={`${css({ flex: 1, overflow: 'hidden' })}`}
    >
      {!paths?.length && loading && (
        <Centered>
          <Spinner />
        </Centered>
      )}
      {!paths?.length && !loading && <EmptyState title="Todavía no cuenta con trayectos este grupo." />}
      {!!paths?.length && (
        <StyledPaginatedTable
          columns={PATHS_BY_GROUP_TABLE_COLUMNS}
          activeId={selectedPathId}
          data={paths.map((path) => {
            const pathByGroup = path as PathItemByGroup;
            return {
              id: pathByGroup._id,
              items: [
                pathByGroup.ident,
                pathByGroup.label,
                formatDateFromTs(pathByGroup.startDate),
                formatDateFromTs(pathByGroup.endDate),
                <FlexColumn
                  gap={0}
                  key={`info-${path._id}`}
                >
                  <ParagraphSmall margin={0}>{path.distanceKms?.toFixed(2)} Km</ParagraphSmall>
                  <ParagraphSmall margin={0}>{formatDurationFromSeconds(path.totalSeconds)}</ParagraphSmall>
                </FlexColumn>,
                <StyledTooltip
                  key={`details-${pathByGroup._id}`}
                  content={'Ver trayecto'}
                  showArrow={false}
                >
                  <Button
                    size="compact"
                    shape="square"
                    onClick={() => setSelectedPathId(pathByGroup._id)}
                    kind="tertiary"
                  >
                    <MaterialIcon name="timeline" />
                  </Button>
                </StyledTooltip>,
              ],
            };
          })}
          totalItems={totalItems}
          page={page}
          onPageChange={setPage}
          minTableWidthSize={650}
        />
      )}
    </FlexColumn>
  );
}
